import { UcoastEl } from '@/scripts/core/UcoastEl'
import { selectRecommendation } from '@/scripts/sections/longevity-results-model'

export class LongevityResultsBuyBox extends UcoastEl {
	static htmlSelector = 'longevity-results-buy-box'
	private static instances = new Set<LongevityResultsBuyBox>()
	private static observer?: MutationObserver
	private static stickyBar?: HTMLElement
	private static stickySpacer?: HTMLElement
	private static stickyResizeObserver?: ResizeObserver
	private static reserveStickySpace = () => {
		const group = LongevityResultsBuyBox
		if (group.stickyBar && group.stickySpacer) {
			// Reserve the actual responsive height after the footer, so its last
			// control can scroll above the fixed bar even when options wrap.
			group.stickySpacer.style.height = `${group.stickyBar.getBoundingClientRect().height}px`
		}
	}
	private static updateStickySpace(boxes: HTMLElement[]) {
		const group = LongevityResultsBuyBox
		const selected = boxes.find(box => box.dataset.ucDesignMode === 'false' && box.dataset.ucRecommendationVisible === 'true')
		const sticky = selected?.querySelector<HTMLElement>('.product__sticky-atc') ?? undefined
		if (sticky !== group.stickyBar) {
			group.stickyResizeObserver?.disconnect()
			group.stickyBar = sticky
			if (sticky) {
				group.stickyResizeObserver ??= new ResizeObserver(group.reserveStickySpace)
				group.stickyResizeObserver.observe(sticky)
			}
		}
		if (!sticky) {
			group.stickySpacer?.remove()
			group.stickySpacer = undefined
			return
		}
		if (!group.stickySpacer) {
			group.stickySpacer = document.createElement('div')
			// Opt out of the theme's reset that hides empty divs.
			group.stickySpacer.classList.add('empty')
			group.stickySpacer.setAttribute('data-uc-results-sticky-spacer', '')
			group.stickySpacer.setAttribute('aria-hidden', 'true')
			document.body.appendChild(group.stickySpacer)
		}
		group.reserveStickySpace()
	}
	private static refresh = () => {
		// Include not-yet-upgraded siblings too: an initial upgrade must not
		// mistake a multi-section page for a single-section direct visit.
		const boxes = Array.from(document.querySelectorAll<HTMLElement>(LongevityResultsBuyBox.htmlSelector))
		const configurations = boxes.map(box => ({ parameter: box.dataset.ucProductParameter ?? '', label: box.dataset.ucProductLabel ?? '', product: box.dataset.ucProductHandle ?? '', defaultBundle: box.dataset.ucDefaultBundle === 'true' }))
		const selection = selectRecommendation(new URLSearchParams(window.location.search), configurations)
		const multipleDefaults = configurations.filter(config => config.defaultBundle).length > 1
		const labels = configurations.map(config => config.label.trim().toLowerCase())
		const invalid = labels.some(label => !label) || new Set(labels).size !== labels.length
			|| configurations.some(config => !config.product.trim() || !config.parameter.trim() || config.parameter.trim() !== configurations[0].parameter.trim())
		const anchor = boxes[0]?.dataset.ucAnchor
		boxes.forEach((box, index) => {
			const editor = box.dataset.ucDesignMode === 'true'
			const selected = editor || selection.index === index
			const fallback = !editor && selection.index === null && index === 0
			const product = box.querySelector<HTMLElement>('[data-uc-recommendation-product]')
			const unavailable = box.querySelector<HTMLElement>('[data-uc-recommendation-unavailable]')
			const wrapper = box.closest<HTMLElement>('[id^="shopify-section-"]') ?? box
			box.dataset.ucRecommendationState = editor ? 'default' : selected ? selection.state : 'unsupported'
			box.dataset.ucRecommendationVisible = String(selected)
			// Only the displayed destination owns the shared hero CTA anchor.
			box.id = !editor && (selected || fallback) && anchor ? anchor : `longevity-buy-box-${index}`
			wrapper.hidden = !selected && !fallback
			if (product) { product.hidden = !selected; product.inert = !selected }
			if (unavailable) unavailable.hidden = !fallback
			const warning = box.querySelector<HTMLElement>('[data-uc-recommendation-editor-warning]')
			if (warning) {
				warning.hidden = !editor
				warning.textContent = multipleDefaults
					? 'Select only one default bundle. Multiple defaults hide all purchase controls and show the browse fallback on the storefront, even when a URL label matches.'
					: invalid
					? 'Use a unique approved label for each bundle and the same recommendation URL parameter for every section. Select a Page product. Ambiguous matches show only the browse fallback on the storefront.'
					: 'Editor preview shows every bundle. A valid Digioh match takes priority; otherwise one default may display for missing, blank or unknown values, without personalized copy. Repeated values and ambiguous matches show the browse fallback. Configure each section’s product, label, media and body together.'
			}
		})
		LongevityResultsBuyBox.updateStickySpace(boxes)
	}
	private guardSubmission = (event: Event) => {
		if (this.dataset.ucRecommendationVisible === 'true') return
		event.preventDefault()
		event.stopImmediatePropagation()
	}
	override connectedCallback() {
		super.connectedCallback()
		this.addEventListener('submit', this.guardSubmission, true)
		const group = LongevityResultsBuyBox
		group.instances.add(this)
		if (!group.observer) {
			window.addEventListener('popstate', group.refresh)
			window.addEventListener('pageshow', group.refresh)
			document.addEventListener('DOMContentLoaded', group.refresh)
			group.observer = new MutationObserver(mutations => {
				if (mutations.some(mutation => mutation.type === 'attributes' || [...mutation.addedNodes, ...mutation.removedNodes].some(node =>
					node instanceof Element && (node.matches(`${group.htmlSelector}, .product__sticky-atc`) || node.querySelector(`${group.htmlSelector}, .product__sticky-atc`)),
				))) group.refresh()
			})
			group.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-uc-product-label', 'data-uc-product-parameter', 'data-uc-product-handle', 'data-uc-default-bundle', 'data-uc-anchor', 'data-uc-design-mode'] })
		}
		group.refresh()
	}
	override disconnectedCallback() {
		this.removeEventListener('submit', this.guardSubmission, true)
		const group = LongevityResultsBuyBox
		group.instances.delete(this)
		group.refresh()
		if (!group.instances.size) {
			group.observer?.disconnect()
			group.observer = undefined
			window.removeEventListener('popstate', group.refresh)
			window.removeEventListener('pageshow', group.refresh)
			document.removeEventListener('DOMContentLoaded', group.refresh)
		}
		super.disconnectedCallback()
	}
}
